import { useEffect } from "react";
import type { ThemeName } from "@huub-web/ui";

/**
 * Themes that use the default Huub favicon.
 * All other themes use their small logo from /assets/images/logos/small/.
 */
const DEFAULT_FAVICON_THEMES: ReadonlySet<ThemeName> = new Set([
  "light",
  "dark",
]);

const SMALL_LOGO_DIR = "/assets/images/logos/small";

const getThemeFaviconPaths = ({
  theme,
}: {
  theme: ThemeName;
}): { svg: string; ico: string; appleTouchIcon: string } => {
  if (DEFAULT_FAVICON_THEMES.has(theme)) {
    return {
      svg: "/favicon.svg",
      ico: "/favicon.ico",
      appleTouchIcon: "/apple-touch-icon.png",
    };
  }

  return {
    svg: `${SMALL_LOGO_DIR}/${theme}.svg`,
    ico: `${SMALL_LOGO_DIR}/${theme}.png`,
    appleTouchIcon: `${SMALL_LOGO_DIR}/${theme}.png`,
  };
};

const updateLinkElement = ({
  selector,
  href,
}: {
  selector: string;
  href: string;
}) => {
  const link = document.querySelector<HTMLLinkElement>(selector);
  if (link) {
    link.href = href;
  }
};

/**
 * Updates the document favicon based on the current theme.
 * Whitelabel themes use their small logo from /assets/images/logos/small/{theme}.
 * Light/dark themes use the default root-level favicons.
 */
export const useFavicon = ({ theme }: { theme: ThemeName }) => {
  useEffect(() => {
    const paths = getThemeFaviconPaths({ theme });

    updateLinkElement({
      selector: 'link[rel="icon"][type="image/svg+xml"]',
      href: paths.svg,
    });
    updateLinkElement({
      selector: 'link[rel="shortcut icon"]',
      href: paths.ico,
    });
    updateLinkElement({
      selector: 'link[rel="apple-touch-icon"]',
      href: paths.appleTouchIcon,
    });
  }, [theme]);
};
